/**
 * Role Preview Banner
 * Shows a floating banner when admin is in role preview mode
 * Allows quick role switching and exiting preview
 */

import { useState } from 'react';
import { useRolePreview, type PreviewRole } from '../lib/RolePreviewContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Eye, 
  X, 
  ChevronDown,
  User, 
  UserCheck, 
  Shield, 
  Building2, 
  Package,
  EyeOff
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const ROLE_OPTIONS: { 
  role: PreviewRole; 
  label: string; 
  icon: typeof User;
}[] = [
  { role: 'none', label: 'Exit Preview (Admin)', icon: Shield },
  { role: 'guest', label: 'Guest (Logged Out)', icon: EyeOff },
  { role: 'patient', label: 'Patient (Unverified)', icon: User },
  { role: 'patient_verified', label: 'Patient (Verified)', icon: UserCheck },
  { role: 'packet_verified', label: 'Packet Verified', icon: Package },
  { role: 'clinic', label: 'Clinic Staff', icon: Building2 },
  { role: 'producer', label: 'Producer', icon: Package },
];

export function RolePreviewBanner() {
  const { 
    previewRole, 
    isPreviewActive, 
    setPreviewRole, 
    clearPreview,
    canUseRolePreview 
  } = useRolePreview();
  
  const [isMinimized, setIsMinimized] = useState(false);

  // Only show if admin and preview is active
  if (!canUseRolePreview || !isPreviewActive) {
    return null;
  }

  const currentRole = ROLE_OPTIONS.find(r => r.role === previewRole);
  const CurrentIcon = currentRole?.icon || Eye;

  if (isMinimized) {
    return (
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100]">
        <Button
          size="sm"
          variant="secondary"
          className="bg-amber-500 hover:bg-amber-600 text-white shadow-lg"
          onClick={() => setIsMinimized(false)}
        >
          <Eye className="w-4 h-4 mr-2" />
          Preview Mode
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-top-2">
      <div className="flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-full shadow-lg">
        <Eye className="w-4 h-4" />
        <span className="text-sm font-medium">Preview Mode:</span>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 px-2 text-white hover:bg-amber-600"
            >
              <CurrentIcon className="w-4 h-4 mr-1" />
              {currentRole?.label}
              <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-56">
            <DropdownMenuLabel>Switch Role Preview</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {ROLE_OPTIONS.map(({ role, label, icon: Icon }) => (
              <DropdownMenuItem
                key={role}
                onClick={() => setPreviewRole(role)}
                className={previewRole === role ? 'bg-accent' : ''}
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
                {previewRole === role && (
                  <Badge variant="secondary" className="ml-auto">
                    Active
                  </Badge>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="w-px h-4 bg-amber-400" />
        
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-white hover:bg-amber-600"
          onClick={() => setIsMinimized(true)}
          title="Minimize banner"
        >
          <ChevronDown className="w-4 h-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-white hover:bg-amber-600"
          onClick={clearPreview}
          title="Exit preview mode"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

